import { type World, tileAt } from '@tama/game-core';
import type { Graphics } from 'pixi.js';
import { TILE } from './layout';
import { C } from './palette';

/**
 * The grid itself: floor boards and wall blocks.
 *
 * Drawn once per world change rather than per frame — only the pet moves, and
 * rebuilding 192 tiles at 60 Hz to animate a walk cycle would be absurd.
 *
 * There is no real 3/4 projection here. A wall is a flat tile with a lit top
 * edge, a shaded base, and a soft shadow cast onto the floor tile below it —
 * three strips that cost nothing and give the room enough depth that walls read
 * as walls instead of as differently-coloured floor.
 */
export function drawTiles(g: Graphics, world: World): void {
  g.clear();

  for (let y = 0; y < world.height; y++) {
    for (let x = 0; x < world.width; x++) {
      if (tileAt(world, x, y) === 'wall') continue;
      drawFloorTile(g, x, y);
    }
  }

  // Walls after every floor tile, so a wall's shadow always falls on top of the
  // floor rather than under the next tile drawn.
  for (let y = 0; y < world.height; y++) {
    for (let x = 0; x < world.width; x++) {
      if (tileAt(world, x, y) !== 'wall') continue;
      drawWallTile(g, world, x, y);
    }
  }
}

function drawFloorTile(g: Graphics, x: number, y: number): void {
  const px = x * TILE;
  const py = y * TILE;

  // Boards run horizontally and alternate shade by row, so the eye can count
  // rows without a hard grid being painted over the whole floor.
  g.rect(px, py, TILE, TILE).fill(y % 2 === 0 ? C.floor : C.floorAlt);
  g.rect(px, py + TILE - 1, TILE, 1).fill({ color: C.floorSeam, alpha: 0.45 });
  // One short vertical seam per tile, offset by row: staggered joints read as
  // planks, aligned ones read as tiles.
  const seamX = px + (y % 2 === 0 ? TILE * 0.32 : TILE * 0.68);
  g.rect(seamX, py, 1, TILE - 1).fill({ color: C.floorSeam, alpha: 0.25 });
}

function drawWallTile(g: Graphics, world: World, x: number, y: number): void {
  const px = x * TILE;
  const py = y * TILE;

  g.rect(px, py, TILE, TILE).fill(C.wall);
  // Lit only where the wall is exposed to the room above it.
  if (tileAt(world, x, y - 1) !== 'wall') {
    g.rect(px, py, TILE, 4).fill(C.wallTop);
  }

  // The face and its shadow only exist where the wall meets open floor.
  if (tileAt(world, x, y + 1) !== 'wall') {
    g.rect(px, py + TILE - 6, TILE, 6).fill(C.wallBase);
    g.rect(px, py + TILE, TILE, 5).fill({ color: C.wallShadow, alpha: 0.12 });
  }

  // A hairline between neighbouring wall tiles, so a long run still reads as
  // masonry rather than as one flat slab.
  if (tileAt(world, x + 1, y) === 'wall') {
    g.rect(px + TILE - 1, py, 1, TILE).fill({ color: C.wallBase, alpha: 0.35 });
  }
}
